<template>
  <div>
    <page-header
      :actions="headerActionConfig"
      left-side-class="col-xl-4 pb-2"
      :page="headerConfig"
      right-side-class="col-xl-8 align-items-end d-flex pb-4 justify-content-end"
    />
    <filter-manager
      ref="filterRef"
      v-model="filterModel"
      class="mb-4"
      :filters="filtersConfig"
      @academicYear="onAcademicYearChange"
      @search="onSearchHandler(1)"
    />
    <custom-separator class="my-4" text="Resultados" />
    <custom-result-not-found
      v-if="results.length == 0"
      text="No cuenta con información disponible."
    />
    <result-row v-for="row in results" :id="row.id" :key="row.id" :data="row" />

    <pagination
      v-if="showPagination"
      :data="pagination"
      show-text
      @pagination-go-page="goToPageHandler"
    />
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import ResultRow from './components/ResultRow.vue';
import {
  getPreliminarSearchInitFilters as _getPreliminarSearchInitFilters,
  getAreas as _getAreas,
  getAnnoncements as _getAnnoncements,
} from '@/modules/proposalProcess/api.js';

import storeMixin from '@/shared/mixins/storeGenerator';
import alertMixin from '@/mixins/alert.mixin.js';

const DEFAULT_PAGE_SIZE = 20;

export default {
  name: 'BidDocumentationMain',
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    ResultRow,
  },
  mixins: [alertMixin, storeMixin],
  data() {
    return {
      filterOptions: {},
      results: [],
      academicYears: [],
      areaOptions: [],
      statuses: [
        { id: 1, name: 'Borrador' },
        { id: 2, name: 'Publicado' },
        { id: 3, name: 'Finalizado' },
      ],
      filterModel: {
        academicYear: this.currentAcademicYear(),
        isFinished: null,
        area: null,
        announcementKey: null,
        startDate: null,
        publishDate: null,
        finishDate: null,
        preAuctionDate: null,
        status: null,
      },
      pagination: {
        currentPage: 1,
        totalItemCount: 1,
        pageCount: 1,
        count: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      },
    };
  },
  computed: {
    headerConfig() {
      return {
        title: 'Documentación de Pliego',
        icon: 'fas fa-city',
        routes: ['Proceso de Competencias', 'Documentación de Pliego'],
      };
    },
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => {
            this.$router.push({ name: 'infraestructure' });
          },
        },
        {
          text: 'Añadir',
          visible: this.canShowAddBid,
          icon: 'fas fa-file-medical',
          variant: 'success',
          click: () => {
            this.$router.push({ name: 'BidDocumentationAdd' });
          },
        },
      ];
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          class: 'col-xl-2 col-lg-6',
          options: this.academicYears,
          initialValue: this.filterModel.academicYear,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: false,
        },
        {
          name: 'isFinished',
          component: 'CustomDropDown',
          placeholder: '¿Está finalizado?',
          options: [
            { id: 1, name: 'Si' },
            { id: 2, name: 'No' },
          ],
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-xl-2 col-lg-6',
          clearable: true,
        },
        {
          name: 'area',
          component: 'CustomDropDown',
          placeholder: 'Seleccione área',
          options: this.areaOptions,
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-xl-4 col-lg-6',
          clearable: true,
        },
        {
          name: 'announcementKey',
          component: 'CustomInput',
          class: 'col-xl-4 col-lg-6',
          placeholder: 'Escriba término clave o número de aviso',
          clearable: true,
          icon: 'fas fa-search color-primary-2',
          leftIcon: true,
        },
        {
          component: 'CustomDatePicker',
          name: 'publishDate',
          placeholder: 'DD/MM/YYYY',
          icon: 'fa fa-search color-orange',
          classContainer: 'w-100',
          leftIcon: true,
          isAdvanced: true,
          class: 'col-md-4',
          label: 'Fecha de Publicación:',
        },
        {
          component: 'CustomDatePicker',
          name: 'startDate',
          placeholder: 'DD/MM/YYYY',
          icon: 'fa fa-search color-orange',
          classContainer: 'w-100',
          leftIcon: true,
          isAdvanced: true,
          class: 'col-md-4',
          label: 'Fecha de Comienzo:',
        },
        {
          component: 'CustomDatePicker',
          name: 'preAuctionDate',
          placeholder: 'DD/MM/YYYY',
          icon: 'fa fa-search color-orange',
          classContainer: 'w-100',
          leftIcon: true,
          isAdvanced: true,
          class: 'col-md-4',
          label: 'Fecha de Presubasta:',
        },
        {
          component: 'CustomDatePicker',
          name: 'finishDate',
          placeholder: 'DD/MM/YYYY',
          icon: 'fa fa-search color-orange',
          classContainer: 'w-100',
          leftIcon: true,
          isAdvanced: true,
          class: 'col-md-4',
          label: 'Fecha de Finalización:',
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          placeholder: 'Seleccione',
          options: this.statuses,
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-md-4',
          clearable: true,
          isAdvanced: true,
          label: 'Estatus:',
        },
      ];
    },
    canShowAddBid() {
      return this.userHasPermissions('provider.biddocumentation.create');
    },
  },
  async mounted() {
    await this.setInitFilters();
    await this.onSearchHandler(1);
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    async setInitFilters() {
      const { data } = await _getPreliminarSearchInitFilters();
      if (Object.values(data).length) {
        this.areaOptions = data.areas;
        this.academicYears = data.academicYears;

        this.fetchAreaOptions();
      }
    },
    async onAcademicYearChange() {
      this.filterModel.area = null;
      this.fetchAreaOptions();
    },
    async fetchAreaOptions() {
      const { data } = await _getAreas(this.filterModel.academicYear?.id);
      this.areaOptions = data;
    },
    async onSearchHandler(page) {
      this.pagination.currentPage = page;

      const { data } = await _getAnnoncements({
        academicYearId: this.filterModel.academicYear.id,
        announcementKey: this.filterModel.announcementKey,
        areaId: this.filterModel.area?.id,
        isFinished: this.filterModel.isFinished?.id,
        publishDate: this.filterModel.publishDate,
        startDate: this.filterModel.startDate,
        finishDate: this.filterModel.finishDate,
        preAuctionDate: this.filterModel.preAuctionDate,
        statusId: this.filterModel.status?.id,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
      });

      this.results = data.data;
      this.pagination = {
        ...this.pagination,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count,
      };
    },
    async goToPageHandler(page) {
      await this.onSearchHandler(page);
    },
  },
};
</script>
